import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useAuth } from '@/providers/AuthProvider';
import { needsOnboarding } from '@/routes/auth/resolveNextRoute';

interface AuthGuardProps {
  children: ReactNode;
  /** When true, skip the onboarding redirect (used by the /onboarding route itself). */
  allowUnboarded?: boolean;
}

export function AuthGuard({ children, allowUnboarded = false }: AuthGuardProps) {
  const { user, isAuthenticated, isBootstrapping } = useAuth();
  const location = useLocation();

  if (isBootstrapping) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-(--color-fg-muted)">
        Carregando…
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    const next = `${location.pathname}${location.search}`;
    const qs = next && next !== '/' ? `?next=${encodeURIComponent(next)}` : '';
    return <Navigate to={`/login${qs}`} replace />;
  }

  if (!allowUnboarded && needsOnboarding(user)) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}
